import { GetStaticPropsContext } from 'next'
import Head from 'next/head'
import { Layout, ProductsListing, Hero } from '@components/common'
import { Container } from '@components/ui'
import Filters from '@components/products/Filters'
import { Breadcrumb, ShopifyCollection, ShopifyProduct } from '@types'
import { getCollections, getProductsInCollection } from '@lib/shopify'
import s from './products.module.css'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'

interface Product {
  node: ShopifyProduct
}

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const products = await getProductsInCollection(
    'home-and-living',
    locale || 'en-US'
  )
  const collections = await getCollections(locale || 'en-US')

  return {
    props: {
      ...(await serverSideTranslations(locale || 'en-US', [
        'common',
        'products',
        'cart',
      ])),
      products: products.map((p: Product) => p.node),
      collections,
    },
  }
}

type CategoryProps = {
  products: ShopifyProduct[]
  category: string
  collections: ShopifyCollection[]
}

export default function HomeAndLiving({
  products,
  collections,
}: CategoryProps) {
  const { t } = useTranslation(['products', 'common'])

  const breadcrumbs: Array<Breadcrumb> = [
    { path: '/', label: t('common:breadcrumbs.home') },
    { path: '/products', label: t('common:breadcrumbs.home') },
    { label: t('common:categories.home-and-living') },
  ]

  return (
    <>
      <Head>
        <title>
          {t('common:metaTitles.productsWithCategory', {
            name: t(`common:categories.home-and-living`),
          })}
        </title>
        <meta
          name="description"
          content={t('common:metaDescriptions.category.home-and-living')}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={t(`common:categories.home-and-living`)}
        />
        <meta
          property="og:image"
          content="https://ennsens.com/assets/meta/og-image.jpg"
        />
        <meta property="og:image:alt" content={t('common:slogan')} />
        <meta
          property="og:description"
          content={t('common:metaDescriptions.category.home-and-living')}
        />
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content={t(`common:categories.home-and-living`)}
        />
        <meta
          name="twitter:description"
          content={t('common:metaDescriptions.category.home-and-living')}
        />
        <meta
          name="twitter:image"
          content="https://ennsens.com/assets/meta/og-image.jpg"
        />
        <meta name="twitter:image:alt" content={t('common:slogan')} />
      </Head>
      <Hero
        title={t('common:categories.home-and-living')}
        breadcrumbs={breadcrumbs}
      />
      <Container className="my-20 md:my-32 lg:my-52">
        <section className={s.mainGrid}>
          <Filters activeCategory="home-and-living" categories={collections} />
          <ProductsListing
            className="mt-10 lg:mt-0"
            products={products}
            withInfo
          />
        </section>
      </Container>
    </>
  )
}

HomeAndLiving.Layout = Layout
